// Angular Imports
import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

// RxJS Imports
import { finalize, Subject, takeUntil } from "rxjs";

// API Imports
import { FileManagerService } from "#services/file-manager/file-manager.service";

// Types Imports
import { FileData, FileManager, FileManagerActionType } from "#models/file-manager";
import { Permission } from "#models/permissions";

// Components Imports
import { LoaderComponent } from "#components/loader/loader.component";

// Variables Imports
import { variables } from "#src/app/shared/variables/variables";

// Translation Imports
import { TranslatePipe } from "#pipes/translate/translate.pipe";

// Services
import { UserService } from "#services/user/user.service";

// Ant Imports
import { ModalOptions, NzModalModule, NzModalService } from "ng-zorro-antd/modal";
import { NzImage, NzImageModule, NzImageService } from "ng-zorro-antd/image";
import { NzUploadFile, NzUploadModule } from "ng-zorro-antd/upload";
import { NzBreadCrumbModule } from "ng-zorro-antd/breadcrumb";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzCardModule } from "ng-zorro-antd/card";
import { NzListModule } from "ng-zorro-antd/list";

//const
export const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"];

@Component({
    selector: "app-file-manager-modal",
    templateUrl: "./file-manager-modal.component.html",
    styleUrls: ["./file-manager-modal.component.scss"],
    imports: [
        NzModalModule,
        NzBreadCrumbModule,
        NzUploadModule,
        NzIconModule,
        NzCardModule,
        NzListModule,
        NzButtonModule,
        NzCollapseModule,
        NzPaginationModule,
        NzImageModule,
        FormsModule,
        CommonModule,
        LoaderComponent,
        TranslatePipe,
    ],
})
export class FileManagerModalComponent implements OnInit, OnDestroy {
    @Input() isVisible = false;
    @Input() isSelectable = true;
    @Input() nzAccept: string[] | undefined = undefined;
    @Input() type: FileManagerActionType = "images";
    @Input() entityName = "global";
    @Input() multiple = false;

    @ViewChild("modalTemplate", { read: ViewContainerRef }) modalTemplate!: ViewContainerRef;

    isLoading = false;
    breadcrumbs: string[] = [];
    folders: string[] = [];
    files: FileData[] = [];
    selectedFiles: Record<FileData["filename"], FileData> = {};
    perPage = 36;
    totalFiles = 0;
    currentPath = "/";
    currentPage = 1;
    fallback = variables.NO_IMAGE;
    modalParams!: ModalOptions;

    @Output() fileSelected = new EventEmitter<FileData>();
    @Output() closeEvent = new EventEmitter<void>();

    private destroy$ = new Subject<void>();
    private fileManagerService = inject(FileManagerService);
    private cdr = inject(ChangeDetectorRef);
    private imageService = inject(NzImageService);
    private userService = inject(UserService);
    private modalService = inject(NzModalService);
    private destroyRef = inject(DestroyRef);
    private i18n = inject(TranslatePipe);

    constructor() {
        TranslatePipe.onLanguageChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.setModalParams();
        });
    }

    get hasSelected() {
        return !!Object.keys(this.selectedFiles).length;
    }

    ngOnInit(): void {
        this.selectedFiles = {};

        this.restorePersistedState();
        this.loadFiles();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async handleOpenCreateFolderDialog() {
        const { CreateFolderModalComponent } =
            await import("#components/create-folder-modal/create-folder-modal.component");
        const ref = this.modalTemplate.createComponent(CreateFolderModalComponent);
        ref.setInput("currentPath", this.currentPath);
        ref.setInput("type", this.type);
        ref.instance.submitEmitter.subscribe(() => {
            this.loadFiles();
        });
        ref.instance.show();
    }

    async handleOpenSearchDialog() {
        const { FileManagerSearchDialogComponent } =
            await import("#components/file-manager/file-manager-search-dialog/file-manager-search-dialog.component");
        const ref = this.modalTemplate.createComponent(FileManagerSearchDialogComponent);

        ref.setInput("currentPath", this.currentPath);
        ref.setInput("type", this.type);

        ref.instance.resultsEmitter.subscribe((result) => {
            if ("type" in result) {
                if (result.type === "folder") {
                    this.navigateToFolder(result.path);
                } else if (result.type === "file") {
                    this.handleFileSelection(result.file);
                }
            }
        });

        ref.instance.show();
    }

    async handleMultipleUpload(): Promise<void> {
        this.handleSetLoading(true);
        const arr = Object.values(this.selectedFiles);
        await Promise.all(arr.map((file) => this.uploadFile(file)));
        this.handleCloseFileManagerModal();
    }

    handleOpenFileManagerModal(): void {
        this.isVisible = true;
    }

    handleCloseFileManagerModal(): void {
        this.isVisible = false;
        this.closeEvent.emit();
    }

    handleSetLoading(state: boolean): void {
        setTimeout(
            () => {
                this.isLoading = state;
                this.cdr.detectChanges();
            },
            state ? 0 : 100
        );
    }

    loadFiles(): void {
        this.handleSetLoading(true);

        this.fileManagerService
            .getFoldersAndFiles(
                {
                    pageIndex: this.currentPage,
                    pageSize: this.perPage,
                    path: this.currentPath,
                    type: this.type,
                },
                () => {
                    this.handleCloseFileManagerModal();
                }
            )
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.handleSetLoading(false))
            )
            .subscribe((response) => {
                const fileManager: FileManager = response.data;
                this.breadcrumbs = fileManager.breadcrumbs;
                this.folders = fileManager.folders;
                this.files = fileManager.files.data;
                this.totalFiles = fileManager.files.meta.total;

                this.persistCurrentState();
            });
    }

    navigateToFolder(path: string): void {
        this.currentPath = path;
        this.currentPage = 1;
        this.persistCurrentState();
        this.loadFiles();
    }

    handleFileSelection(file: FileData): void {
        if (this.multiple) {
            if (this.selectedFiles[file.filename]) {
                delete this.selectedFiles[file.filename];
            } else {
                this.selectedFiles[file.filename] = file;
            }
        } else {
            this.handleSetLoading(true);
            this.uploadFile(file).then(() => {
                this.handleSetLoading(false);
                this.handleCloseFileManagerModal();
            });
        }
    }

    uploadFile(file: FileData): Promise<void> {
        return new Promise((resolve) => {
            this.fileManagerService
                .getFile({ path: file.path, type: this.type })
                .pipe(finalize(() => this.handleSetLoading(false)))
                .subscribe(({ data }) => {
                    const fullFileData = { ...file, ...data };
                    this.fileSelected.emit(fullFileData);
                    resolve();
                });
        });
    }

    deleteFile(file: FileData) {
        this.modalService.confirm({
            ...this.modalParams,
            nzOkDanger: true,
            nzTitle: this.i18n.transform("i18n.global.actions.delete"),
            nzOnOk: () =>
                new Promise((resolve) => {
                    this.handleSetLoading(true);

                    this.fileManagerService
                        .deleteFile({
                            path: file.path,
                            type: this.type,
                        })
                        .subscribe({
                            next: ({ message }) => {
                                this.fileManagerService.messageService.success(message);
                                resolve(true);
                            },
                            complete: () => {
                                this.loadFiles();
                            },
                        });
                }),
        });
    }

    handleImagePreview(image: NzImage, file: FileData): void {
        if (this.isImage(file)) {
            this.imageService.preview([image], {
                nzZoom: 0.9,
                nzScaleStep: 0.25,
            });
        } else {
            window.open(file.url, "_blank");
        }
    }

    handleBeforeUpload = (file: NzUploadFile): boolean => {
        this.handleFileUpload(file);
        return false;
    };

    handleFileUpload(file: NzUploadFile): void {
        this.handleSetLoading(true);
        this.fileManagerService
            .uploadFile(this.currentPath, file, this.type)
            .pipe(finalize(() => this.handleSetLoading(false)))
            .subscribe(() => this.loadFiles());
    }

    handleChangePageIndex(page: number): void {
        this.currentPage = page;
        this.persistCurrentState();
        this.loadFiles();
    }

    handleChangePageSize(size: number): void {
        this.perPage = size;
        this.currentPage = 1;
        this.persistCurrentState();
        this.loadFiles();
    }

    getExtension(name: string): string {
        return name.split(".").pop() || "";
    }

    getIsSelectable(file: FileData) {
        const ext = this.getExtension(file.filename);
        const accept = !this.nzAccept || this.nzAccept.includes(`.${ext}`);
        const isImageValidation =
            this.type !== "images" || (this.type === "images" && this.isImage(file));
        return this.isSelectable && accept && isImageValidation;
    }

    isImage(file: FileData) {
        const ext = this.getExtension(file.filename);
        return imageExtensions.includes(`.${ext}`);
    }

    private restorePersistedState(): void {
        if (this.entityName === "global") {
            this.currentPath = "/";
            this.currentPage = 1;
            return;
        }
        const state = this.fileManagerService.getPersistedState(this.entityName);
        this.currentPath = state.path;
        this.currentPage = state.page;
    }

    private persistCurrentState(): void {
        if (this.entityName === "global") {
            return;
        }

        this.fileManagerService.setPersistedState(this.entityName, {
            path: this.currentPath,
            page: this.currentPage,
        });
    }

    can(permission: Permission): boolean {
        return this.userService.can(permission);
    }

    private setModalParams() {
        this.modalParams = {
            nzOkType: "primary",
            nzContent: this.i18n.transform("i18n.global.dialogs.confirm_subtitle"),
            nzOkText: this.i18n.transform("i18n.global.yes"),
            nzCancelText: this.i18n.transform("i18n.global.no"),
        };
    }
}
